import React, { useState, useEffect } from 'react';
import { TreeView, TreeViewDataItem, Button, Switch, Alert } from '@patternfly/react-core';
import { shouldForceDependencies } from '../helpers/clusterConfig';
import { StatusLabel, getServiceStatus, getInfrastructureStatus } from './StatusLabel';

interface DependencySelectorProps {
  selectedBusinessUnits: string[];
  checkboxStates: Record<string, boolean>;
  setCheckboxStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  businessData: any;
  deployedServices?: string[]; 
  standaloneMode?: boolean;
  skipQuota?: boolean; // indicates namespace is already running
}

const MAX_SERVICE_DEPTH = 2;

/**
 * PASS 1:
 * Mark required dependencies in serviceRequiredMap or infraRequiredMap.
 * If the release for a dependency is missing, we still treat it as “service”.
 */
function markRequiredDependencies(
  serviceRelease: any,
  parentIsRequired: boolean,
  path: Set<string>,
  serviceRequiredMap: Record<string, boolean>,
  infraRequiredMap: Record<string, boolean>,
  businessData: any,
  deployedSet: Set<string>  
): void {
  if (path.has(serviceRelease.name)) return;
  path.add(serviceRelease.name);

  if (parentIsRequired && !deployedSet.has(serviceRelease.name)) {
    serviceRequiredMap[serviceRelease.name] = true;
  }

  for (const dep of serviceRelease.dependencies || []) {
    const depRelease = businessData.releases.find((r: any) => r.name === dep.name);
    const childIsRequired = parentIsRequired && !!dep.required;

    // If no release is found, treat it as a forced service if required.
    if (!depRelease) {
      if (childIsRequired && !deployedSet.has(dep.name)) {
        serviceRequiredMap[dep.name] = true;
      }
      continue;
    }

    // No depth limit here — MAX_SERVICE_DEPTH caps only the UI tree (buildServiceTreeNode).
    // We must walk ALL transitively-required deps so they land in checkboxStates
    // and flow correctly into the Review step.
    // Only walk required paths — optional subtrees can never contribute marked deps.
    if (!childIsRequired) continue;

    if (depRelease.type === 'infrastructure') {
      if (!deployedSet.has(depRelease.name)) {
        infraRequiredMap[depRelease.name] = true;
      }
    } else {
      if (!deployedSet.has(depRelease.name)) {
        serviceRequiredMap[depRelease.name] = true;
      }
      markRequiredDependencies(
        depRelease,
        childIsRequired,
        new Set(path),
        serviceRequiredMap,
        infraRequiredMap,
        businessData,
        deployedSet
      );
    }
  }
}

/**
 * PASS 2:
 * Build TreeView nodes. Infrastructure is put in infraSet for separate grouping.
 * If no release is found, create a “placeholder” node.
 */
function buildServiceTreeNode(
  serviceRelease: any,
  level: number,
  path: Set<string>,
  serviceRequiredMap: Record<string, boolean>,
  infraRequiredMap: Record<string, boolean>,
  infraSet: Set<string>,
  checkboxStates: Record<string, boolean>,
  setCheckboxStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  businessData: any,
  parentChecked: boolean,
  deployedSet: Set<string>,
  shouldNeverForce: boolean = false,
  standaloneMode: boolean = false,
  enforceEnabled: boolean = true
): TreeViewDataItem | null {
  if (path.has(serviceRelease.name)) return null;
  path.add(serviceRelease.name);

  const isTopLevel = level === 1;
  const isForcedByLogic = !isTopLevel && !!serviceRequiredMap[serviceRelease.name];
  const shouldBeChecked = isTopLevel
  ? !!checkboxStates[serviceRelease.name]
  : parentChecked && (checkboxStates[serviceRelease.name] || (isForcedByLogic && enforceEnabled));

  // Ensure forced dependencies are in checkboxStates when they should be checked
  if (shouldBeChecked && !checkboxStates[serviceRelease.name]) {
    setCheckboxStates(prev => ({ ...prev, [serviceRelease.name]: true }));
  }

  const nodeDisabled = isTopLevel
    ? false
    : !parentChecked && !isForcedByLogic;

  const onChangeHandler = isTopLevel || (!nodeDisabled && (!isForcedByLogic || !enforceEnabled))
    ? () => {
        setCheckboxStates((prev) => {
          const newVal = !prev[serviceRelease.name];
          const newState = { ...prev, [serviceRelease.name]: newVal };
          // On prod-like + running namespace, never force required deps onto the user
          if (shouldNeverForce) {
            return newState;
          }
          // Recursively propagate to all required dependencies (not just 1 level deep)
          const svcReq: Record<string, boolean> = {};
          const infraReq: Record<string, boolean> = {};
          markRequiredDependencies(
            serviceRelease,
            true,
            new Set<string>(),
            svcReq,
            infraReq,
            businessData,
            deployedSet
          );
          Object.keys(svcReq).forEach(name => { newState[name] = newVal; });
          Object.keys(infraReq).forEach(name => { newState[name] = newVal; });
          return newState;
        });
      }
    : undefined;

  const nodeStyle = nodeDisabled
    ? { color: 'gray', opacity: 0.6, cursor: 'not-allowed' }
    : {};

  const children: TreeViewDataItem[] = [];

  // Recursively build children or gather infra
  if (level < MAX_SERVICE_DEPTH) {
        for (const dep of serviceRelease.dependencies || []) {
          const id = dep.name;
          const style = { color: 'gray', opacity: 0.6, cursor: 'not-allowed' };
          const depRelease = businessData.releases.find(r => r.name === id);
          // collect infra separately
          if (depRelease?.type === 'infrastructure') {
            infraSet.add(id);
            continue;
          }
          const isRequired    = dep.required;
          const isParentOn    = parentChecked;
          const userChecked   = !!checkboxStates[id];
          const isDepRunning = deployedSet.has(id);

          let checked, disabled, onChange, childStyle;
    
          if (isParentOn) {
            if (isRequired && !isDepRunning && !shouldNeverForce && enforceEnabled) {
              checked = true;           // force only if NOT deployed AND not on preprod running ns
              disabled = true;
              childStyle = { color: 'gray', opacity: 0.6, cursor: 'not-allowed' };
            } else {
              checked = userChecked;    // deployed or optional → user controls
              disabled = false;
              childStyle = {};
              onChange = () => setCheckboxStates(p => ({ ...p, [id]: !p[id] }));
            }
          } else {
            checked = userChecked;
            disabled = false;
            childStyle = {};
            onChange = () => setCheckboxStates(p => ({ ...p, [id]: !p[id] }));
          }
    
          children.push({
            id, name: id,
            checkProps: { checked, disabled, style: childStyle, onChange }
          });
        }
      } else {
    // At max depth, just gather infra
    for (const dep of serviceRelease.dependencies || []) {
      const depRelease = businessData.releases.find((r: any) => r.name === dep.name);
      if (!depRelease) {
        // Note: shouldNeverForce check would need to be passed down, but for simplicity we keep this as-is
        const forced = shouldBeChecked && dep.required && !deployedSet.has(dep.name);
        children.push({
          id: dep.name,
          name: dep.name,
          checkProps: {
            checked: forced,
            disabled: forced,
            style: forced ? { color: 'gray', opacity: 0.6, cursor: 'not-allowed' } : {}
          }
        });
      } else if (depRelease.type === 'infrastructure') {
        infraSet.add(depRelease.name);
      }
    }
  }

  // Calculate service status once for efficiency
  const serviceStatus = standaloneMode ? getServiceStatus(serviceRelease.name, Array.from(deployedSet), []) : null;
  const statusDescription = serviceStatus ? 
    `Service: ${serviceRelease.name} - ${serviceStatus === 'running' ? 'Already deployed' : serviceStatus === 'outdated' ? 'Deployed but outdated' : 'Not deployed'}` : 
    '';

  return {
    id: serviceRelease.name,
    name: standaloneMode && serviceStatus ? (
      <div className="dx-flex dx-flex--gap-sm">
        <span>{serviceRelease.name}</span>
        <span>
          <StatusLabel 
            status={serviceStatus} 
            name={serviceRelease.name}
            description={statusDescription}
          />
        </span>
      </div>
    ) : serviceRelease.name,
    checkProps: {
      checked: shouldBeChecked,
      disabled: nodeDisabled,
      style: nodeStyle,
      onChange: onChangeHandler
    },
    children: children.length > 0 ? children : undefined
  };
}

const DependencySelector: React.FC<DependencySelectorProps> = ({
  selectedBusinessUnits,
  checkboxStates,
  setCheckboxStates,
  businessData,
  deployedServices = [],
  standaloneMode = false,
  skipQuota = false
}) => {
  const [treeData, setTreeData] = useState<TreeViewDataItem[]>([]);
  const [enforceEnabled, setEnforceEnabled] = useState(true);
  const deployedSet = React.useMemo(() => new Set(deployedServices ?? []), [deployedServices]);
  
  // On prod-like clusters: never force deps (they use cloud services, not shared infra).
  // On non-prod clusters with a running namespace (standalone): also never force.
  const canForce = shouldForceDependencies(skipQuota);
  const shouldNeverForce = !canForce;

  // bulk-toggle helpers
  const selectAll = () => {
    setCheckboxStates(prev => {
      const next = { ...prev };
      const svcReq: Record<string, boolean> = {};
      const infraReq: Record<string, boolean> = {};

      selectedBusinessUnits.forEach(exp => {
        // only services part of this experience
        const topLevel = businessData.releases.filter(r => {
          const exps = r.part_of_product || [];
          return exps.includes(exp) || exps.includes('*');
        });
        const topServices = topLevel.filter(r => r.type === 'service' || !r.type);
        topServices.forEach(svc => {
          next[svc.name] = true;
          // recursively mark all “required” children
          markRequiredDependencies(
            svc,
            true,
            new Set<string>(),
            svcReq,
            infraReq,
            businessData,
            deployedSet
          );
        });
      });

      // select all services that were marked “required”
      Object.keys(svcReq).forEach(name => {
        next[name] = true;
      });
      // select all infra that were marked “required”
      Object.keys(infraReq).forEach(name => {
        next[name] = true;
      });
      return next;
    });
  };

  const unselectAll = () => {
    setCheckboxStates(prev => {
      const next = { ...prev };
      const toClear = new Set<string>();

      selectedBusinessUnits.forEach(exp => {
        const topLevel = businessData.releases.filter(r => {
          const exps = r.part_of_product || [];
          return exps.includes(exp) || exps.includes('*');
        });
        const topServices = topLevel.filter(r => r.type === 'service' || !r.type);
        topServices.forEach(svc => {
          toClear.add(svc.name);
          // also clear all required children for this svc
          const svcReq: Record<string, boolean> = {};
          const infraReq: Record<string, boolean> = {};
          markRequiredDependencies(
            svc,
            true,
            new Set<string>(),
            svcReq,
            infraReq,
            businessData,
            deployedSet
          );
          Object.keys(svcReq).forEach(n => toClear.add(n));
          Object.keys(infraReq).forEach(n => toClear.add(n));
        });
        // additionally, clear top-level infra for this experience
        const topInfra = topLevel.filter(r => r.type === 'infrastructure');
        topInfra.forEach(infra => toClear.add(infra.name));
      });

      toClear.forEach(key => {
        next[key] = false;
      });
      return next;
    });
  }; 

  useEffect(() => {
    const generateTreeData = (): TreeViewDataItem[] => {
      const deployedSet = new Set<string>(deployedServices);

      return selectedBusinessUnits.map((experienceName) => {
        // Gather top-level releases for this experience
        const topLevelReleases = businessData.releases.filter((r: any) => {
          const exps = r.part_of_product || [];
          return exps.includes(experienceName) || exps.includes('*');
        });

        // Treat missing or “service” type as top-level services
        const topLevelServices = topLevelReleases.filter(
          (r: any) => r.type === 'service' || !r.type
        );

        // For infra, we show all from businessData (like you already do)
        const topLevelInfra = businessData.releases.filter(
          (r: any) => r.type === 'infrastructure'         
        );

        const serviceRequiredMap: Record<string, boolean> = {};
        const infraRequiredMap: Record<string, boolean> = {};

        // Mark required only from selected, non-running services in standalone
        topLevelServices.forEach((svc) => {
          const isSelected = !!checkboxStates[svc.name];
          const isRunning  = deployedServices.includes(svc.name);

          const shouldPropagate =
            isSelected && (!standaloneMode || !isRunning); // block running services in standalone

          if (shouldPropagate) {
            markRequiredDependencies(
              svc,
              true,
              new Set<string>(),
              serviceRequiredMap,
              infraRequiredMap,
              businessData,
              deployedSet
            );
          }
        });

        if (!standaloneMode) {
          businessData.releases.forEach((r: any) => {
            if ((r.part_of_product || []).includes('*')) {
              if (r.type === 'infrastructure') {
                if (!deployedSet.has(r.name)) infraRequiredMap[r.name] = true;
              } else {
                if (!deployedSet.has(r.name)) serviceRequiredMap[r.name] = true;
              }
            }
          });
        }

        // Build service nodes & gather infra
        const serviceNodes: TreeViewDataItem[] = [];
        const infraSet = new Set<string>();

        topLevelServices.forEach((svc) => {
        // if this service is already running, render it as a flat leaf
          const isRunning = deployedSet.has(svc.name);

          let node: TreeViewDataItem | null;
          if (standaloneMode && isRunning) {
            // just show the parent checkbox, no children - use status label instead of color
            const serviceStatus = getServiceStatus(svc.name, Array.from(deployedSet), []);
            const statusDescription = `Service: ${svc.name} - ${serviceStatus === 'running' ? 'Already deployed' : serviceStatus === 'outdated' ? 'Deployed but outdated' : 'Not deployed'}`;
            
            node = {
              id: svc.name,
              name: (
                <div className="dx-flex dx-flex--gap-sm">
                  <span>{svc.name}</span>
                  <span>
                    <StatusLabel 
                      status={serviceStatus} 
                      name={svc.name}
                      description={statusDescription}
                    />
                  </span>
                </div>
              ),
              checkProps: {
                checked: checkboxStates[svc.name],
                onChange: () => setCheckboxStates(p => ({
                  ...p,
                  [svc.name]: !p[svc.name]
                }))
              }
            };
          } else {
            // normal recursive build
            node = buildServiceTreeNode(
              svc,
              1,
              new Set<string>(),
              serviceRequiredMap,
              infraRequiredMap,
              infraSet,
              checkboxStates,
              setCheckboxStates,
              businessData,
              !!checkboxStates[svc.name],
              deployedSet,
              shouldNeverForce,
              standaloneMode,
              enforceEnabled
            );
            
            // Remove old color styling - status labels handle this now
            // if (standaloneMode && node && !isRunning) {
            //   node.checkProps = {
            //     ...node.checkProps,
            //     style: { ...node.checkProps?.style, color: '#F0AB00', fontWeight: 'bold' }
            //   };
            // }
          }

          if (node) {
            serviceNodes.push(node);
          }
        });

        // Add top-level infra
        topLevelInfra.forEach((inf) => {
          if (!infraRequiredMap.hasOwnProperty(inf.name)) {
            infraRequiredMap[inf.name] = false;
          }
          infraSet.add(inf.name);
        });

        // Convert infraSet to TreeView items
        const infraChildren: TreeViewDataItem[] = Array.from(infraSet).map((infraName) => {
          const isForced = !!infraRequiredMap[infraName];
          const shouldBeChecked = checkboxStates[infraName] || (isForced && !deployedSet.has(infraName) && !shouldNeverForce && enforceEnabled);
          const nodeDisabled = isForced && !deployedSet.has(infraName) && !shouldNeverForce && enforceEnabled;
          const isDeployed = deployedSet.has(infraName);
          
          // Ensure forced dependencies are in checkboxStates when they should be checked
          if (shouldBeChecked && !checkboxStates[infraName]) {
            setCheckboxStates(prev => ({ ...prev, [infraName]: true }));
          }
          
          
          let itemStyle: React.CSSProperties = {};
          if (nodeDisabled) {
            itemStyle = { color: 'gray', opacity: 0.6, cursor: 'not-allowed' };
          }
          
          // Get infrastructure status for display (only in standalone mode)
          const infraStatus = standaloneMode ? getInfrastructureStatus(infraName, Array.from(deployedSet)) : null;
          const infraDescription = infraStatus ? 
            `Infrastructure: ${infraName} - ${infraStatus === 'running' ? 'Already deployed' : 'Not deployed'}` : 
            '';
          
          return {
            id: infraName,
            name: standaloneMode && infraStatus ? (
              <div className="dx-flex dx-flex--gap-sm">
                <span>{infraName}</span>
                <span>
                  <StatusLabel 
                    status={infraStatus} 
                    name={infraName}
                    description={infraDescription}
                  />
                </span>
              </div>
            ) : infraName,
            checkProps: {
              checked: shouldBeChecked,
              disabled: nodeDisabled,
              style: itemStyle,
              onChange: !nodeDisabled
                ? () => {
                    setCheckboxStates((prev) => ({
                      ...prev,
                      [infraName]: !prev[infraName]
                    }));
                  }
                : undefined
            }
          };
        });

        // alphabetize services by id
        serviceNodes.sort((a, b) =>
          (a.id as string).localeCompare(b.id as string)
        );

        // alphabetize infra by id
        infraChildren.sort((a, b) =>
          (a.id as string).localeCompare(b.id as string)
        );

        // Combine service + infra children
        const children: TreeViewDataItem[] = [];
        if (serviceNodes.length > 0) {
          children.push({
            id: `${experienceName}-services`,
            name: `Services (${serviceNodes.length})`,
            checkProps: { style: { display: 'none' } },
            children: serviceNodes
          });
        }
        if (infraChildren.length > 0) {
          children.push({
            id: `${experienceName}-infrastructure`,
            name: `Infrastructure (${infraChildren.length})`,
            checkProps: { style: { display: 'none' } },
            children: infraChildren
          });
        }

        return {
          id: experienceName,
          name: experienceName,
          checkProps: { style: { display: 'none' } },
          children
        };
      });
    };

    setTreeData(generateTreeData());
  }, [selectedBusinessUnits, businessData, checkboxStates, setCheckboxStates, deployedServices, standaloneMode, enforceEnabled]);

  // persist step2 selections to localStorage, clear stale reviewData
  useEffect(() => {
    // Only save items that are actually in checkboxStates
    // This ensures step2Selections exactly matches what's visually checked
    const payload = {
      selectedBusinessUnits,
      selectedDependencies: Object.entries(checkboxStates)
        .filter(([name, checked]) => {
          if (!checked) return false;
          // keep only real releases (services/infra) from businessData
          return businessData.releases.some((r: any) => r.name === name);
        })
        .map(([name]) => ({ name })),
    };

    const prevRaw = localStorage.getItem('step2Selections');
    let prev: typeof payload | null = null;
    try { prev = prevRaw ? JSON.parse(prevRaw) : null; } catch {}

    const changed =
      !prev ||
      JSON.stringify(prev.selectedBusinessUnits) !== JSON.stringify(payload.selectedBusinessUnits) ||
      JSON.stringify(prev.selectedDependencies) !== JSON.stringify(payload.selectedDependencies);

    if (changed) {
      localStorage.setItem('step2Selections', JSON.stringify(payload));
      localStorage.removeItem('reviewData');
    }
  }, [selectedBusinessUnits, checkboxStates]);

  // If a node has a custom onChange, use it; otherwise toggle
  const onCheck = (_event: React.ChangeEvent<HTMLInputElement>, item: TreeViewDataItem) => {
    if (item.checkProps?.onChange) {
      item.checkProps.onChange(_event);
    } else if (item.id) {
      setCheckboxStates((prev) => ({ ...prev, [item.id as string]: !prev[item.id as string] }));
    }
  };

  return (
    <div style={{ padding: '10px' }}>
      {standaloneMode && deployedServices && deployedServices.length > 0 && (
        <Alert variant="info" isInline title="Standalone Mode - Service Status" style={{ marginBottom: '1rem' }}>
          <p><strong>Running</strong> = Already deployed services. Dependencies are NOT pre-selected since they're already running.</p>
          <p><strong>Not Deployed</strong> = New services to deploy. Required dependencies WILL be pre-selected automatically.</p>
        </Alert>
      )}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div className="dx-flex dx-flex--gap-sm">
          <div>
            <Button variant="secondary" onClick={selectAll}>
              Select All
            </Button>
          </div>
          <div>
            <Button variant="link" onClick={unselectAll}>
              Unselect All
            </Button>
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Switch
            id="override-deps-switch"
            label="Enforce required dependencies"
            isChecked={enforceEnabled}
            onChange={(_event, checked) => setEnforceEnabled(checked)}
          />
        </div>
      </div>
      {!enforceEnabled && (
        <Alert variant="warning" isInline component='div' title="Required dependencies are not enforced. Deploy may fail if dependencies are missing." style={{ paddingTop: 8, paddingBottom: 8, marginTop: 12, marginBottom: 8}} />
      )}
      {treeData.length === 0 ? (
        <p>No releases found for the selected experiences.</p>
      ) : (
        <TreeView data={treeData} hasCheckboxes onCheck={onCheck} />
      )}
    </div>
  );
};

export default DependencySelector;

  // The component is pretty straightforward. It receives the selected business units, the checkbox states, the business data, and a function to update the checkbox states. It then renders the business units and their dependencies. 
  // The  toggleSubDependencies  function is used to toggle the sub-dependencies of a dependency. The  handleCheckboxChange  function is used to update the checkbox state of a dependency. 
  // The component is used in the  Dashboard  component.
